import React, { useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, addDoc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, where, onSnapshot, serverTimestamp, orderBy } from 'firebase/firestore';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// --- Firebase Configuration ---
// IMPORTANT: PASTE YOUR FIREBASE CONFIGURATION OBJECT HERE
const firebaseConfig = {
  apiKey: "AIzaSyAg2VnhHUsgn7_Vxnrh-C2_AmK4fTiYGBg",
  authDomain: "saigon-staff-performance.firebaseapp.com",
  projectId: "saigon-staff-performance",
  storageBucket: "saigon-staff-performance.firebasestorage.app",
  messagingSenderId: "1:92774581221:web:e0c65dd1f1df9b1ab7650c",
  appId: "G-QWKKMC69NE"
};

// --- Initialize Firebase ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- Global App ID ---
const appId = 'staff-performance-app';

// --- Constants ---
const ROLES = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  LEADER: 'Leader',
  STAFF: 'Staff',
};

const DEFAULT_PASSWORDS = {
  [ROLES.ADMIN]: 'Admin1819',
  [ROLES.MANAGER]: 'Admin1811',
  [ROLES.LEADER]: '0852',
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// --- Helper: Modal Component ---
const Modal = ({ children, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-md md:max-w-2xl relative">
            <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
            {children}
        </div>
    </div>
);


const App = () => {
  // --- State Management ---
  const [currentUser, setCurrentUser] = useState(null);
  const [currentRole, setCurrentRole] = useState(ROLES.STAFF);
  const [users, setUsers] = useState([]);
  const [points, setPoints] = useState([]);
  const [categories, setCategories] = useState(['A', 'B', 'C']);
  const [passwords, setPasswords] = useState(DEFAULT_PASSWORDS);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('login');
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // --- Refs & Modals ---
  const inactivityTimer = useRef(null);
  const [modal, setModal] = useState({ type: null, data: null });

  // --- Inactivity Logout ---
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (currentRole !== ROLES.STAFF) {
      inactivityTimer.current = setTimeout(() => {
        setCurrentRole(ROLES.STAFF);
        setView('login');
        showNotification('Logged out due to inactivity.', 'info');
      }, 5 * 60 * 1000); // 5 minutes
    }
  }, [currentRole]);

  useEffect(() => {
    resetInactivityTimer();
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('keypress', resetInactivityTimer);
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keypress', resetInactivityTimer);
    };
  }, [resetInactivityTimer]);


  // --- Firebase Auth & Data Listeners ---
  useEffect(() => {
    // Dynamically load PDF generation libraries
    const loadScript = (src, id) => {
        if (!document.getElementById(id)) {
            const script = document.createElement('script');
            script.id = id;
            script.src = src;
            script.async = true;
            document.head.appendChild(script);
        }
    };
    loadScript('https://unpkg.com/jspdf@latest/dist/jspdf.umd.min.js', 'jspdf-script');
    loadScript('https://unpkg.com/jspdf-autotable@3.5.23/dist/jspdf.plugin.autotable.js', 'jspdf-autotable-script');


    const authAndFetch = async () => {
      try {
        await signInAnonymously(auth);
      } catch (error) {
        console.error("Authentication Error:", error);
        showNotification("Authentication failed. Please refresh.", "error");
      }
    };
    authAndFetch();

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const adminDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', 'TRUNG');
        const adminDocSnap = await getDoc(adminDocRef);
        if (!adminDocSnap.exists()) {
            await setDoc(adminDocRef, { name: 'TRUNG', role: ROLES.ADMIN });
        }
        
        setCurrentUser(user);
        
        onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'users'), (snapshot) => setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))));
        onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'points'), (snapshot) => setPoints(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))));

        const configCollection = collection(db, 'artifacts', appId, 'public', 'data', 'config');
        onSnapshot(doc(configCollection, 'categories'), (docSnap) => {
            if (docSnap.exists()) setCategories(docSnap.data().list);
            else setDoc(doc(configCollection, 'categories'), { list: ['Quality', 'Teamwork', 'Punctuality'] });
        });
        onSnapshot(doc(configCollection, 'passwords'), (docSnap) => {
            if (docSnap.exists()) setPasswords(docSnap.data());
            else setDoc(doc(configCollection, 'passwords'), DEFAULT_PASSWORDS);
        });

      } else {
        setCurrentUser(null);
        setCurrentRole(ROLES.STAFF);
        setView('login');
      }
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 3000);
  };
  
  const handleLogin = (password) => {
    let newRole = '';
    if (password === passwords[ROLES.ADMIN]) newRole = ROLES.ADMIN;
    else if (password === passwords[ROLES.MANAGER]) newRole = ROLES.MANAGER;
    else if (password === passwords[ROLES.LEADER]) newRole = ROLES.LEADER;

    if (newRole) {
      setCurrentRole(newRole);
      setView('dashboard');
      showNotification(`Logged in as ${newRole}`, 'success');
    } else {
      showNotification('Incorrect password', 'error');
    }
  };

  const handleNavClick = (newView) => {
    setView(newView);
    setIsMenuOpen(false);
  }
  
  // --- Render Logic ---
  const renderView = () => {
    if (loading) return <div className="flex justify-center items-center h-screen"><div className="text-xl">Loading...</div></div>;
    
    switch(view) {
      case 'login': return <LoginScreen handleLogin={handleLogin} setView={setView} setRole={setCurrentRole} />;
      case 'dashboard': return <Dashboard setModal={setModal} users={users} role={currentRole} />;
      case 'manageStaff': return <StaffManagement users={users} role={currentRole} showNotification={showNotification} setModal={setModal} />;
      case 'teamSummary': return <TeamPointsSummary points={points} users={users} showNotification={showNotification} />;
      case 'leaderBoard': return <LeaderBoard points={points} users={users} showNotification={showNotification}/>;
      case 'saigonStar': return <SaigonStar users={users} showNotification={showNotification} role={currentRole} />;
      case 'settings': return <Settings showNotification={showNotification} currentCategories={categories} currentPasswords={passwords} />;
      default: return <LoginScreen handleLogin={handleLogin} setView={setView} setRole={setCurrentRole} />;
    }
  };

  const navLinks = [
    { name: 'Point Assignment', view: 'dashboard', roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.LEADER, ROLES.STAFF] },
    { name: 'Staff Management', view: 'manageStaff', roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.LEADER] },
    { name: 'SAIGON STAR', view: 'saigonStar', roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.LEADER, ROLES.STAFF] },
    { name: 'Team Summary', view: 'teamSummary', roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.LEADER, ROLES.STAFF] },
    { name: 'Leader Board', view: 'leaderBoard', roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.LEADER, ROLES.STAFF] },
    { name: 'Settings', view: 'settings', roles: [ROLES.ADMIN] },
  ];

  const availableNavLinks = navLinks.filter(link => link.roles.includes(currentRole));

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      {(view !== 'login') && (
        <nav className="bg-white shadow-md sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <span className="font-bold text-lg sm:text-xl text-blue-600">SAIGON STAFF PERFORMANCE</span>
              {/* Desktop Menu */}
              <div className="hidden md:flex items-baseline space-x-2">
                  {availableNavLinks.map(link => (
                      <button key={link.view} onClick={() => handleNavClick(link.view)} className={`px-3 py-2 rounded-md text-sm font-medium ${view === link.view ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}>{link.name}</button>
                  ))}
              </div>
              <div className="hidden md:flex items-center">
                <span className="text-gray-600 mr-4 text-sm">Role: {currentRole}</span>
                <button onClick={() => { setView('login'); setCurrentRole(ROLES.STAFF); }} className="bg-red-500 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-red-600">Logout</button>
              </div>
              {/* Mobile Menu Button */}
              <div className="md:hidden flex items-center">
                  <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-md text-gray-700 hover:bg-gray-200">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
                  </button>
              </div>
            </div>
          </div>
           {/* Mobile Menu */}
           {isMenuOpen && (
              <div className="md:hidden">
                  <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                      {availableNavLinks.map(link => (
                          <button key={link.view} onClick={() => handleNavClick(link.view)} className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${view === link.view ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}>{link.name}</button>
                      ))}
                  </div>
                  <div className="pt-4 pb-3 border-t border-gray-200">
                      <div className="flex items-center px-5">
                          <span className="text-gray-600 mr-4 text-sm">Role: {currentRole}</span>
                      </div>
                      <div className="mt-3 px-2 space-y-1">
                          <button onClick={() => { handleNavClick('login'); setCurrentRole(ROLES.STAFF); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-200">Logout</button>
                      </div>
                  </div>
              </div>
           )}
        </nav>
      )}

      {notification.message && (
        <div className={`fixed top-20 right-5 p-4 rounded-lg shadow-lg text-white z-50 ${notification.type === 'success' ? 'bg-green-500' : notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'}`}>
            {notification.message}
        </div>
      )}

      {modal.type && (
          <Modal onClose={() => setModal({ type: null, data: null })}>
              {modal.type === 'addPoint' && <PointEntryForm user={modal.data} categories={categories} assignerRole={currentRole} assignerId={currentUser.uid} showNotification={showNotification} closeModal={() => setModal({type: null, data: null})} />}
              {modal.type === 'editPoint' && <PointEntryForm user={modal.data.user} categories={categories} assignerRole={currentRole} assignerId={currentUser.uid} showNotification={showNotification} closeModal={() => setModal({type: null, data: null})} existingPoint={modal.data.point} />}
              {modal.type === 'viewHistory' && <HistoryView user={modal.data} points={points} users={users} setModal={setModal} currentRole={currentRole} showNotification={showNotification} />}
              {modal.type === 'editUser' && <EditUserForm user={modal.data} showNotification={showNotification} closeModal={() => setModal({type: null, data: null})} currentRole={currentRole} />}
          </Modal>
      )}

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {renderView()}
      </main>
    </div>
  );
};


// --- Components ---

const LoginScreen = ({ handleLogin, setView, setRole }) => {
    const [password, setPassword] = useState('');
    const onSubmit = (e) => { e.preventDefault(); handleLogin(password); };
    return (
        <div className="flex flex-col justify-center items-center h-[80vh]">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm text-center">
                <h1 className="text-2xl font-bold mb-6 text-gray-700">Staff Performance</h1>
                <p className="text-gray-500 mb-6">Enter password for privileged access or continue as Staff.</p>
                <form onSubmit={onSubmit}>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full p-3 mb-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                    <button type="submit" className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Login</button>
                </form>
                <button onClick={() => { setView('leaderBoard'); setRole(ROLES.STAFF); }} className="w-full mt-4 bg-gray-200 text-gray-700 p-3 rounded-lg font-semibold hover:bg-gray-300">Continue as Staff</button>
            </div>
        </div>
    );
};

const Dashboard = ({ setModal, users, role }) => {
    const canManageUserForPointAssignment = (targetUserRole) => {
        if (role === ROLES.ADMIN) return true;
        if (role === ROLES.MANAGER && (targetUserRole === ROLES.LEADER || targetUserRole === ROLES.STAFF)) return true;
        if (role === ROLES.LEADER && targetUserRole === ROLES.STAFF) return true;
        return false;
    };
    
    const displayUsers = users.filter(u => {
        // Staff can see other Staff and Leaders for history view.
        if (role === ROLES.STAFF) {
            return u.role === ROLES.STAFF || u.role === ROLES.LEADER;
        }
        // Higher roles can see who they can assign points to.
        return canManageUserForPointAssignment(u.role) && u.role !== ROLES.ADMIN && u.role !== ROLES.MANAGER;
    });

    return (
        <div className="p-4">
            <h1 className="text-3xl font-bold mb-6">{role === ROLES.STAFF ? 'Team Overview' : 'Point Assignment'}</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayUsers.map(user => (
                    <div key={user.id} className="bg-white p-5 rounded-lg shadow-md flex flex-col justify-between">
                        <div>
                            <h3 className="text-xl font-semibold">{user.name}</h3>
                            <p className="text-gray-500">{user.role}</p>
                        </div>
                        <div className="mt-4 flex space-x-2">
                            {role !== ROLES.STAFF && (
                                <button onClick={() => setModal({ type: 'addPoint', data: user })} className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">Add Points</button>
                            )}
                            <button onClick={() => setModal({ type: 'viewHistory', data: user })} className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300">History</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StaffManagement = ({ users, role, showNotification, setModal }) => {
    const [name, setName] = useState('');
    const [newRole, setNewRole] = useState(ROLES.STAFF);
    const [password, setPassword] = useState('');
    const [bulkData, setBulkData] = useState('');
    
    const canCreateRole = (targetRole) => {
        if (role === ROLES.ADMIN) return true;
        if (role === ROLES.MANAGER && (targetRole === ROLES.LEADER || targetRole === ROLES.STAFF)) return true;
        if (role === ROLES.LEADER && targetRole === ROLES.STAFF) return true;
        return false;
    };

    const handleAddUser = async () => {
        if (!name.trim()) return showNotification('User name is required.', 'error');
        if (!canCreateRole(newRole)) return showNotification('Permission denied to create this role.', 'error');
        try {
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'users'), { name, role: newRole });
            showNotification('User added!', 'success');
            setName('');
        } catch(e) { console.error(e); showNotification('Failed to add user.', 'error'); }
    };
    
    const handleDeleteUser = async (userId, userName) => {
       if (userName === 'TRUNG') return showNotification('Cannot delete root admin.', 'error');
       if (window.confirm(`Are you sure you want to delete ${userName}?`)) {
           try {
               await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', userId));
               showNotification('User deleted.', 'success');
           } catch (e) { console.error(e); showNotification('Failed to delete user.', 'error');}
       }
    };

    const handleBulkAdd = async () => {
        const lines = bulkData.split('\n').filter(line => line.trim() !== '');
        let successCount = 0;
        let errorCount = 0;
        for (const line of lines) {
            const [bulkName, bulkRole] = line.split('\t');
            if (bulkName && bulkRole && Object.values(ROLES).includes(bulkRole) && canCreateRole(bulkRole)) {
                try {
                    await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'users'), { name: bulkName, role: bulkRole });
                    successCount++;
                } catch { errorCount++; }
            } else { errorCount++; }
        }
        showNotification(`${successCount} users added. ${errorCount} failed.`, errorCount > 0 ? 'error' : 'success');
        setBulkData('');
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold">Staff Management</h1>
            {(role === ROLES.ADMIN || role === ROLES.MANAGER || role === ROLES.LEADER) && (
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h2 className="text-xl font-semibold mb-4">Add Single User</h2>
                        <div className="space-y-4">
                            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="User Name" className="w-full p-3 border rounded-lg"/>
                            <select value={newRole} onChange={e => setNewRole(e.target.value)} className="w-full p-3 border rounded-lg">
                                {canCreateRole(ROLES.ADMIN) && <option value={ROLES.ADMIN}>Admin</option>}
                                {canCreateRole(ROLES.MANAGER) && <option value={ROLES.MANAGER}>Manager</option>}
                                {canCreateRole(ROLES.LEADER) && <option value={ROLES.LEADER}>Leader</option>}
                                <option value={ROLES.STAFF}>Staff</option>
                            </select>
                            <button onClick={handleAddUser} className="w-full bg-green-500 text-white p-3 rounded-lg font-semibold hover:bg-green-600">Add User</button>
                        </div>
                    </div>
                    {(role === ROLES.ADMIN || role === ROLES.MANAGER) && <div className="bg-white p-6 rounded-lg shadow-md">
                        <h2 className="text-xl font-semibold mb-4">Multiple Staff Entry</h2>
                        <textarea value={bulkData} onChange={e => setBulkData(e.target.value)} placeholder="Paste from spreadsheet (Name [tab] Role per line)" rows="5" className="w-full p-3 border rounded-lg"></textarea>
                        <p className="text-xs text-gray-500 mt-1">Example: John Doe [tab] Staff</p>
                        <button onClick={handleBulkAdd} className="mt-2 w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600">Bulk Add</button>
                    </div>}
                 </div>
            )}
            
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">User List</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-100">
                            <tr><th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">Actions</th></tr>
                        </thead>
                        <tbody>
                            {users.filter(u => role === ROLES.ADMIN ? true : (role === ROLES.MANAGER ? u.role !== ROLES.ADMIN : u.role === ROLES.STAFF)).map(u => (
                                <tr key={u.id} className="border-b">
                                    <td className="p-3">{u.name}</td><td className="p-3">{u.role}</td>
                                    <td className="p-3 space-x-2">
                                        <button onClick={() => setModal({ type: 'editUser', data: u })} className="text-blue-500 hover:text-blue-700">Edit</button>
                                        <button onClick={() => handleDeleteUser(u.id, u.name)} className="text-red-500 hover:text-red-700">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const PointEntryForm = ({ user, categories, assignerRole, assignerId, showNotification, closeModal, existingPoint }) => {
    const [type, setType] = useState(existingPoint?.type || 'Plus');
    const [category, setCategory] = useState(existingPoint?.category || categories[0] || '');
    const [pointsValue, setPointsValue] = useState(existingPoint?.points || 1);
    const [note, setNote] = useState(existingPoint?.note || '');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!note.trim()) return showNotification('A descriptive note is required.', 'error');
        if (pointsValue <= 0) return showNotification('Points must be a positive number.', 'error');

        const pointData = {
            userId: user.id, userName: user.name, type, category, note,
            points: Number(pointsValue), assignerRole, assignerId, createdAt: existingPoint?.createdAt || serverTimestamp()
        };

        try {
            if(existingPoint) {
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'points', existingPoint.id), pointData);
                showNotification('Point updated successfully!', 'success');
            } else {
                await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'points'), pointData);
                showNotification('Point added successfully!', 'success');
            }
            closeModal();
        } catch (error) {
            console.error("Error saving point:", error);
            showNotification('Failed to save point.', 'error');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-2xl font-bold">{existingPoint ? 'Edit' : 'Add'} Point for {user.name}</h2>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block font-semibold">Point Type</label>
                    <select value={type} onChange={e => setType(e.target.value)} className="w-full p-3 border rounded-lg mt-1">
                        <option value="Plus">Plus (Good Performance)</option>
                        <option value="Minus">Minus (Needs Improvement)</option>
                    </select>
                </div>
                 <div>
                    <label className="block font-semibold">Points</label>
                    <input type="number" value={pointsValue} onChange={e => setPointsValue(e.target.value)} min="1" className="w-full p-3 border rounded-lg mt-1"/>
                </div>
            </div>
            <div>
                <label className="block font-semibold">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="w-full p-3 border rounded-lg mt-1">
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
            </div>
            <div>
                <label className="block font-semibold">Note</label>
                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Reason for the point..." rows="4" className="w-full p-3 border rounded-lg mt-1"></textarea>
            </div>
            <button type="submit" className="w-full bg-green-500 text-white p-3 rounded-lg font-semibold hover:bg-green-600">Save Point</button>
        </form>
    );
};

const HistoryView = ({ user, points, setModal, currentRole, showNotification }) => {
    const userPoints = points.filter(p => p.userId === user.id).sort((a,b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
    const canEdit = currentRole === ROLES.ADMIN || currentRole === ROLES.MANAGER;

    const handleDeletePoint = async (pointId) => {
        if(window.confirm("Are you sure you want to delete this point entry?")) {
            try {
                await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'points', pointId));
                showNotification("Point deleted!", "success");
            } catch(error) {
                showNotification("Failed to delete point.", "error");
            }
        }
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-4">Point History for {user.name}</h2>
            <div className="max-h-[60vh] overflow-y-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-100 sticky top-0"><tr><th className="p-2">Date</th><th className="p-2">Type</th><th className="p-2">Points</th><th className="p-2">Category</th><th className="p-2">Note</th><th className="p-2">Assigned By</th>{canEdit && <th className="p-2">Actions</th>}</tr></thead>
                    <tbody>
                        {userPoints.length > 0 ? userPoints.map(p => (
                            <tr key={p.id} className="border-b">
                                <td className="p-2 text-sm">{p.createdAt?.toDate().toLocaleDateString()}</td>
                                <td className={`p-2 font-semibold ${p.type === 'Plus' ? 'text-green-600' : 'text-red-600'}`}>{p.type}</td>
                                <td className={`p-2 font-semibold ${p.type === 'Plus' ? 'text-green-600' : 'text-red-600'}`}>{p.points}</td>
                                <td className="p-2">{p.category}</td>
                                <td className="p-2 text-sm">{p.note}</td>
                                <td className="p-2 text-sm">{p.assignerRole}</td>
                                {canEdit && <td className="p-2 space-x-2">
                                    <button onClick={() => setModal({type: 'editPoint', data: {user, point: p}})} className="text-blue-500 hover:text-blue-700">Edit</button>
                                    <button onClick={() => handleDeletePoint(p.id)} className="text-red-500 hover:text-red-700">Delete</button>
                                </td>}
                            </tr>
                        )) : ( <tr><td colSpan={canEdit ? 7 : 6} className="text-center p-4">No points recorded.</td></tr> )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const EditUserForm = ({ user, showNotification, closeModal, currentRole }) => {
    const [name, setName] = useState(user.name);
    const [role, setRole] = useState(user.role);
    const [password, setPassword] = useState('');

    const handleUpdate = async () => {
        if(user.name === 'TRUNG' && (name !== 'TRUNG' || role !== ROLES.ADMIN)) return showNotification('Cannot modify the root admin TRUNG.', 'error');
        if (currentRole !== ROLES.ADMIN && role === ROLES.ADMIN) return showNotification('Only an Admin can assign the Admin role.', 'error');

        const userDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', user.id);
        const updateData = { name, role };
        if (password) updateData.password = password;

        try {
            await updateDoc(userDocRef, updateData);
            showNotification('User updated successfully!', 'success');
            closeModal();
        } catch (error) { console.error(error); showNotification('Failed to update user.', 'error'); }
    };

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold">Edit User: {user.name}</h2>
            <div>
                <label className="block font-semibold">Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full p-3 border rounded-lg mt-1" />
            </div>
            <div>
                <label className="block font-semibold">Role</label>
                <select value={role} onChange={e => setRole(e.target.value)} className="w-full p-3 border rounded-lg mt-1" disabled={user.name === 'TRUNG'}>
                    {currentRole === ROLES.ADMIN && <option value={ROLES.ADMIN}>Admin</option>}
                    <option value={ROLES.MANAGER}>Manager</option><option value={ROLES.LEADER}>Leader</option><option value={ROLES.STAFF}>Staff</option>
                </select>
            </div>
            <div>
                <label className="block font-semibold">New Password (optional)</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank to keep unchanged" className="w-full p-3 border rounded-lg mt-1" />
            </div>
            <button onClick={handleUpdate} className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600">Update User</button>
        </div>
    );
};

const StarIcon = () => (<svg className="w-8 h-8 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>);

const Confetti = () => {
    useEffect(() => {
        const container = document.getElementById('confetti-container');
        if (!container) return;
        for (let i = 0; i < 100; i++) {
            const confetti = document.createElement('div');
            confetti.className = 'confetti-piece';
            confetti.style.left = `${Math.random() * 100}%`;
            confetti.style.animationDelay = `${Math.random() * 5}s`;
            confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            container.appendChild(confetti);
        }
    }, []);
    return <div id="confetti-container" className="absolute inset-0 overflow-hidden pointer-events-none"></div>;
};

const SaigonStar = ({ users, showNotification, role }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [starSelections, setStarSelections] = useState({});
    const [loading, setLoading] = useState(true);
    const [showAllMonths, setShowAllMonths] = useState(false);

    const isManagerOrAdmin = role === ROLES.ADMIN || role === ROLES.MANAGER;
    const staffAndLeaders = users.filter(u => u.role === ROLES.STAFF || u.role === ROLES.LEADER);

    const getMonthInfo = (year) => {
        const now = new Date();
        now.setFullYear(year);
        const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        
        return [
            { name: MONTHS[lastMonthDate.getMonth()], year: lastMonthDate.getFullYear() },
            { name: MONTHS[prevMonthDate.getMonth()], year: prevMonthDate.getFullYear() }
        ];
    };
    
    const [featuredMonths, setFeaturedMonths] = useState(getMonthInfo(selectedYear));

    useEffect(() => {
        setLoading(true);
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'saigonStars', String(selectedYear));
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setStarSelections(docSnap.data());
            } else {
                setStarSelections({});
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching Saigon Star data:", error);
            setLoading(false);
        });
        setFeaturedMonths(getMonthInfo(selectedYear));
        return () => unsubscribe();
    }, [selectedYear]);

    const handleSelectionChange = async (month, userId, isChecked) => {
        const currentSelection = starSelections[month] || [];
        let newSelection = isChecked ? [...currentSelection, userId] : currentSelection.filter(id => id !== userId);
        const docRef = doc(db, 'artifacts', appId, 'public', 'data', 'saigonStars', String(selectedYear));
        try {
            await setDoc(docRef, { [month]: newSelection }, { merge: true });
            showNotification(`Updated Stars for ${month}!`, 'success');
        } catch (error) { showNotification("Failed to save selection.", "error"); }
    };
    
    const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Unknown';

    if (loading) return <p>Loading Hall of Fame...</p>;

    const allMonthsForYear = MONTHS.filter(m => !featuredMonths.some(fm => fm.name === m && fm.year === selectedYear));
    
    const WinnerDisplay = ({month, year}) => {
        const winners = (starSelections[month] || []);
        return (
             <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl shadow-2xl text-center glow relative">
                <Confetti />
                <h2 className="text-3xl font-bold text-center text-yellow-500 mb-4">Star(s) of {month} {year}</h2>
                <div className="flex justify-center items-center gap-4 mb-4"><StarIcon /><StarIcon /><StarIcon /></div>
                {winners.length > 0 ? (
                    <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
                        {winners.map(id => <p key={id} className="text-3xl font-bold text-white">{getUserName(id)}</p>)}
                    </div>
                ) : ( <p className="text-xl text-gray-400">Not yet announced!</p> )}
            </div>
        )
    }

    return (
        <div className="relative">
             <style>{`.confetti-piece{position:absolute;width:8px;height:16px;opacity:0;animation:fall 5s linear forwards}@keyframes fall{0%{top:-20px;opacity:1;transform:rotate(0deg)}100%{top:100vh;opacity:1;transform:rotate(720deg)}}.glow{animation:glow-animation 2s ease-in-out infinite}@keyframes glow-animation{0%{box-shadow:0 0 5px #fde047,0 0 10px #fde047,0 0 15px #fde047}50%{box-shadow:0 0 20px #facc15,0 0 30px #facc15,0 0 40px #facc15}100%{box-shadow:0 0 5px #fde047,0 0 10px #fde047,0 0 15px #fde047}}`}</style>
            <div className="text-center mb-8">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-800">SAIGON KITCHEN STARS</h1>
                 <div className="flex items-center justify-center gap-4 mt-4">
                    <button onClick={() => setSelectedYear(y => y - 1)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg">&lt;</button>
                    <span className="text-2xl font-bold">{selectedYear}</span>
                    <button onClick={() => setSelectedYear(y => y + 1)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg">&gt;</button>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                {featuredMonths.map(month => <WinnerDisplay key={month.name} month={month.name} year={month.year}/>)}
            </div>
            <hr className="my-8"/>

            {isManagerOrAdmin ? (
                <div className="space-y-6">
                    <h2 className="text-2xl font-semibold text-center mb-4">Select Monthly Winners for {selectedYear}</h2>
                    {MONTHS.map(month => (
                        <div key={month} className="bg-white border rounded-lg p-4 shadow">
                            <h3 className="text-xl font-semibold mb-3">{month}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-48 overflow-y-auto">
                                {staffAndLeaders.map(user => (
                                    <label key={user.id} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                                        <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={(starSelections[month] || []).includes(user.id)} onChange={(e) => handleSelectionChange(month, user.id, e.target.checked)}/>
                                        <span>{user.name}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="mt-4 text-sm text-gray-600"><strong>Selected: </strong>{(starSelections[month] || []).map(id => getUserName(id)).join(', ') || 'None'}</div>
                        </div>
                    ))}
                </div>
            ) : (
                <div>
                     <div className="text-center mb-6">
                        <button onClick={() => setShowAllMonths(!showAllMonths)} className="bg-blue-500 text-white font-semibold px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors">
                            {showAllMonths ? 'Hide Past Months' : `Show Full Hall of Fame for ${selectedYear}`}
                        </button>
                    </div>
                     {showAllMonths && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {allMonthsForYear.map(month => {
                             const selectedIds = starSelections[month] || [];
                             if (selectedIds.length === 0) return null;
                             return (
                                <div key={month} className="bg-white p-6 rounded-lg shadow-lg text-center transform hover:scale-105 transition-transform duration-300">
                                    <h3 className="text-xl font-bold text-blue-600 mb-3">{month}</h3>
                                    <div className="flex justify-center mb-3"> <StarIcon /></div>
                                    <div className="space-y-1">{selectedIds.map(id => (<p key={id} className="text-lg font-semibold text-gray-800">{getUserName(id)}</p>))}</div>
                                </div>
                             )
                        })}
                    </div>}
                </div>
            )}
        </div>
    );
};
  
const TeamPointsSummary = ({ points, users, showNotification }) => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    const filteredPoints = points.filter(p => {
        if (!p.createdAt) return false;
        const pointDate = p.createdAt.toDate();
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Include the whole end day
        return pointDate >= start && pointDate <= end;
    });

    const summary = filteredPoints.reduce((acc, point) => {
        if(!acc[point.userId]) {
            const user = users.find(u => u.id === point.userId);
            if (!user || user.role === ROLES.ADMIN || user.role === ROLES.MANAGER) return acc;
            acc[point.userId] = { name: user.name, role: user.role, plus: 0, minus: 0 };
        }
        if(point.type === 'Plus') acc[point.userId].plus += point.points || 0;
        else acc[point.userId].minus += point.points || 0;
        return acc;
    }, {});

    const summaryData = Object.values(summary);

    const exportToCSV = () => {
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Name,Role,Plus Points,Minus Points\n";
        summaryData.forEach(item => {
            csvContent += `${item.name},${item.role},${item.plus},${item.minus}\n`;
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `team_summary_${startDate}_to_${endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showNotification('CSV exported!', 'success');
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h1 className="text-3xl font-bold mb-4">Team Points Summary</h1>
            <div className="flex flex-wrap gap-4 items-end bg-gray-100 p-4 rounded-lg mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 p-2 border rounded-md"/>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 p-2 border rounded-md"/>
                </div>
                <button onClick={exportToCSV} className="bg-green-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">Export CSV</button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-200">
                        <tr>
                            <th className="p-3">Name</th><th className="p-3">Role</th>
                            <th className="p-3 text-green-600">Plus Points</th><th className="p-3 text-red-600">Minus Points</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summaryData.map(item => (
                            <tr key={item.name} className="border-b">
                                <td className="p-3 font-semibold">{item.name}</td><td className="p-3">{item.role}</td>
                                <td className="p-3 text-green-600 font-bold">{item.plus}</td><td className="p-3 text-red-600 font-bold">{item.minus}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const LeaderBoard = ({ points, users, showNotification }) => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
    
    const summary = points
        .filter(p => {
            if (!p.createdAt) return false;
            const pointDate = p.createdAt.toDate();
            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999); // Include the whole end day
            return pointDate >= start && pointDate <= end;
        })
        .reduce((acc, point) => {
            const user = users.find(u => u.id === point.userId);
            if (!user || user.role === ROLES.ADMIN || user.role === ROLES.MANAGER) return acc;
            if(!acc[point.userId]) acc[point.userId] = { name: user.name, role: user.role, plus: 0, minus: 0 };
            
            if(point.type === 'Plus') acc[point.userId].plus += point.points || 0;
            else acc[point.userId].minus += point.points || 0;
            return acc;
        }, {});
        
    const goodPerformers = Object.values(summary).sort((a,b) => b.plus - a.plus).filter(u => u.plus > 0);
    const needImprovement = Object.values(summary).sort((a,b) => b.minus - a.minus).filter(u => u.minus > 0);

    const exportToPDF = () => {
        if (typeof window.jspdf === 'undefined' || typeof window.jspdf.jsPDF === 'undefined' || typeof window.jspdf.jsPDF.API.autoTable !== 'function') {
            showNotification('PDF library is still loading. Please try again in a moment.', 'info');
            return;
        }
        const doc = new window.jspdf.jsPDF();
        doc.text("Performance Leaderboard", 14, 15);
        
        doc.setFontSize(16);
        doc.text("Good Performance (Plus Points)", 14, 25);
        doc.autoTable({
            startY: 30,
            head: [['Rank', 'Name', 'Role', 'Plus Points']],
            body: goodPerformers.map((u, i) => [i + 1, u.name, u.role, u.plus]),
            theme: 'striped',
            headStyles: { fillColor: [22, 160, 133] }
        });

        doc.setFontSize(16);
        doc.text("Need Improvement (Minus Points)", 14, doc.autoTable.previous.finalY + 15);
        doc.autoTable({
            startY: doc.autoTable.previous.finalY + 20,
            head: [['Rank', 'Name', 'Role', 'Minus Points']],
            body: needImprovement.map((u, i) => [i + 1, u.name, u.role, u.minus]),
            theme: 'striped',
            headStyles: { fillColor: [192, 57, 43] }
        });

        doc.save(`leaderboard_${startDate}_to_${endDate}.pdf`);
        showNotification('PDF exported!', 'success');
    };

    const LeaderboardTable = ({ title, data, pointType, colorClass }) => (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className={`text-2xl font-bold mb-4 ${colorClass}`}>{title}</h2>
            <table className="w-full text-left">
                <thead className="bg-gray-200">
                    <tr><th className="p-3">Rank</th><th className="p-3">Name</th><th className="p-3">Role</th><th className="p-3">{pointType}</th></tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={item.name} className="border-b">
                            <td className="p-3 font-bold text-lg">{index + 1}</td>
                            <td className="p-3 font-semibold">{item.name}</td><td className="p-3">{item.role}</td>
                            <td className={`p-3 font-bold ${colorClass}`}>{item[pointType.toLowerCase().split(' ')[0]]}</td>
                        </tr>
                    ))}
                    {data.length === 0 && <tr><td colSpan="4" className="text-center p-4">No data for this period.</td></tr>}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold">Leader Boards</h1>
             <div className="flex flex-wrap gap-4 items-end bg-gray-100 p-4 rounded-lg mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 p-2 border rounded-md"/>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 p-2 border rounded-md"/>
                </div>
                <button onClick={exportToPDF} className="bg-blue-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">Export PDF</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <LeaderboardTable title="Good Performance" data={goodPerformers} pointType="Plus" colorClass="text-green-600" />
                <LeaderboardTable title="Need Improvement" data={needImprovement} pointType="Minus" colorClass="text-red-600" />
            </div>
        </div>
    );
};

const Settings = ({ showNotification, currentCategories, currentPasswords }) => {
    const [managerPassword, setManagerPassword] = useState('');
    const [leaderPassword, setLeaderPassword] = useState('');
    const [newCategory, setNewCategory] = useState('');

    const handlePasswordSave = async () => {
        const passwordUpdates = {};
        if (managerPassword) passwordUpdates.Manager = managerPassword;
        if (leaderPassword) passwordUpdates.Leader = leaderPassword;

        if (Object.keys(passwordUpdates).length > 0) {
            const passwordDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'passwords');
            try {
                await updateDoc(passwordDocRef, passwordUpdates);
                showNotification("Passwords updated successfully!", 'success');
                setManagerPassword('');
                setLeaderPassword('');
            } catch (error) {
                console.error("Error updating passwords:", error);
                showNotification("Failed to update passwords.", "error");
            }
        }
    };
    
    const handleAddCategory = async () => {
        if (!newCategory.trim() || currentCategories.includes(newCategory.trim())) return;
        const updatedCategories = [...currentCategories, newCategory.trim()];
        const catDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'categories');
        await setDoc(catDocRef, { list: updatedCategories });
        setNewCategory('');
        showNotification('Category added!', 'success');
    };

    const handleDeleteCategory = async (categoryToDelete) => {
        const updatedCategories = currentCategories.filter(c => c !== categoryToDelete);
        const catDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'config', 'categories');
        await setDoc(catDocRef, { list: updatedCategories });
        showNotification('Category removed!', 'success');
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold">Admin Settings</h1>
            {/* Password Management */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">Change Role Passwords</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block font-medium">Manager Password</label>
                        <input type="password" value={managerPassword} onChange={e => setManagerPassword(e.target.value)} placeholder="Enter new Manager password" className="mt-1 w-full p-3 border rounded-lg"/>
                    </div>
                    <div>
                        <label className="block font-medium">Leader Password</label>
                        <input type="password" value={leaderPassword} onChange={e => setLeaderPassword(e.target.value)} placeholder="Enter new Leader password" className="mt-1 w-full p-3 border rounded-lg"/>
                    </div>
                    <button onClick={handlePasswordSave} className="w-full bg-blue-500 text-white p-3 rounded-lg font-semibold hover:bg-blue-600">Save Passwords</button>
                </div>
            </div>
            
            {/* Category Management */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">Customize Point Categories</h2>
                <div className="flex gap-2 mb-4">
                    <input type="text" value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="New Category Name" className="flex-grow p-3 border rounded-lg"/>
                    <button onClick={handleAddCategory} className="bg-green-500 text-white p-3 rounded-lg font-semibold hover:bg-green-600">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {currentCategories.map(cat => (
                        <div key={cat} className="bg-gray-200 px-3 py-1 rounded-full flex items-center gap-2">
                            <span>{cat}</span>
                            <button onClick={() => handleDeleteCategory(cat)} className="text-red-500 hover:text-red-800 font-bold">&times;</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


export default App;